import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';

interface UpcomingQuestion {
  id: string;
  topicSlot?: number;
  mainTopic?: string;
  questionText: string;
  category: string;
  difficulty: string;
  correctAnswer: string;
  lastAskedDate: string | null;
  isNiche: boolean;
}

interface UpcomingDateEntry {
  date: string;
  questions: UpcomingQuestion[];
}

/** Group questions by parent topic for display */
function groupQuestionsByTopic(questions: UpcomingQuestion[]): Map<string, UpcomingQuestion[]> {
  const byTopic = new Map<string, UpcomingQuestion[]>();
  for (const q of questions) {
    const topic = q.mainTopic || 'Other';
    if (!byTopic.has(topic)) byTopic.set(topic, []);
    byTopic.get(topic)!.push(q);
  }
  // Sort topics by slot order (1-5)
  const topicOrder = ['Pop Culture', 'Science & Nature', 'History & Politics', 'Geography & World', 'Sports & Games'];
  const sorted = new Map<string, UpcomingQuestion[]>();
  for (const name of topicOrder) {
    if (byTopic.has(name)) sorted.set(name, byTopic.get(name)!);
  }
  for (const [name, qs] of byTopic) {
    if (!sorted.has(name)) sorted.set(name, qs);
  }
  return sorted;
}

/** Get per-topic counts for summary display */
function getTopicCounts(questions: UpcomingQuestion[]): { topic: string; count: number }[] {
  const byTopic = new Map<string, number>();
  for (const q of questions) {
    const topic = q.mainTopic || 'Other';
    byTopic.set(topic, (byTopic.get(topic) || 0) + 1);
  }
  const order = ['Pop Culture', 'Science & Nature', 'History & Politics', 'Geography & World', 'Sports & Games'];
  return order.map((t) => ({ topic: t, count: byTopic.get(t) || 0 })).filter((x) => x.count > 0);
}

interface QuestionSearchResult {
  id: string;
  question_text: string;
  category: string;
  main_topic: string | null;
  difficulty: string;
  correct_answer: string;
  last_asked_date: string | null;
  isNiche: boolean;
}

const UPCOMING_WINDOW_DAYS = 14;
const EXPECTED_QUESTIONS_PER_DAY = 50; // 5 topics × 10 questions each

const AdminUpcomingCompetitive: React.FC = () => {
  useDocumentTitle('Cognidaily | Upcoming Competitive');

  const [dates, setDates] = useState<UpcomingDateEntry[]>([]);
  const [todayDate, setTodayDate] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rebuildingDate, setRebuildingDate] = useState<string | null>(null);
  const [workingDate, setWorkingDate] = useState<string | null>(null);

  const [addModalDate, setAddModalDate] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addModalTopic, setAddModalTopic] = useState<string>('');
  const [searchResults, setSearchResults] = useState<QuestionSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const MAIN_TOPICS = ['Pop Culture', 'Science & Nature', 'History & Politics', 'Geography & World', 'Sports & Games'];

  const formatLastAskedDate = (value: string | null) => {
    if (!value) return 'Never';
    return value.slice(0, 10);
  };

  const loadUpcoming = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get<{
        success: boolean;
        data: { dates: UpcomingDateEntry[]; startDate: string };
        message?: string;
      }>('/api/admin/competitive/upcoming', { params: { days: UPCOMING_WINDOW_DAYS } });

      if (!res.data.success) {
        throw new Error(res.data.message || 'Failed to load upcoming quizzes');
      }
      setDates(res.data.data.dates || []);
      setTodayDate(res.data.data.startDate || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load upcoming quizzes.');
    } finally {
      setLoading(false);
    }
  }, []);

  const rebuildDate = async (date: string) => {
    try {
      setRebuildingDate(date);
      setError(null);
      const res = await axios.post<{
        success: boolean;
        data?: { date: string; questions: UpcomingQuestion[] };
        message?: string;
      }>(`/api/admin/competitive/upcoming/${date}/rebuild`);

      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.message || 'Failed to rebuild question set');
      }

      setDates((prev) =>
        prev.map((entry) =>
          entry.date === date ? { ...entry, questions: res.data.data!.questions } : entry
        )
      );
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to rebuild question set.';
      if (errMsg.includes('Not enough questions')) {
        try {
          const genRes = await axios.post<{
            success: boolean;
            data?: { totalCandidatesCreated: number };
          }>('/api/admin/generate-for-gaps', { maxPerSubtopic: 10 });
          const created = genRes.data?.data?.totalCandidatesCreated ?? 0;
          setError(
            `${errMsg} Generated ${created} candidate questions. Approve them in Question Review, then try Rebuild set again.`
          );
        } catch (genErr: any) {
          setError(
            `${errMsg} Could not auto-generate: ${genErr?.response?.data?.message || genErr?.message || 'unknown error'}.`
          );
        }
      } else {
        setError(errMsg);
      }
    } finally {
      setRebuildingDate(null);
    }
  };

  const addQuestion = async (date: string, questionId: string) => {
    const trimmedId = questionId.trim();
    if (!trimmedId) {
      setSearchError('Choose a question to add.');
      return;
    }

    try {
      setWorkingDate(date);
      const res = await axios.post<{
        success: boolean;
        data?: { date: string; questions: UpcomingQuestion[] };
        message?: string;
      }>(`/api/admin/competitive/upcoming/${date}/questions`, { questionId: trimmedId });

      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.message || 'Failed to add question');
      }

      setDates((prev) =>
        prev.map((entry) =>
          entry.date === date ? { ...entry, questions: res.data.data!.questions } : entry
        )
      );
    } catch (err: any) {
      setSearchError(err?.response?.data?.message || err?.message || 'Failed to add question.');
    } finally {
      setWorkingDate(null);
    }
  };

  const removeQuestion = async (date: string, questionId: string) => {
    try {
      setWorkingDate(date);
      const res = await axios.delete<{
        success: boolean;
        data?: { date: string; questions: UpcomingQuestion[] };
        message?: string;
      }>(`/api/admin/competitive/upcoming/${date}/questions/${questionId}`);

      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.message || 'Failed to remove question');
      }

      setDates((prev) =>
        prev.map((entry) =>
          entry.date === date ? { ...entry, questions: res.data.data!.questions } : entry
        )
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to remove question.');
    } finally {
      setWorkingDate(null);
    }
  };

  const toggleRow = (date: string) => {
    setExpandedDate((prev) => (prev === date ? null : date));
  };

  const openAddModal = (date: string) => {
    setAddModalDate(date);
    setSearchQuery('');
    setAddModalTopic('');
    setSearchResults([]);
    setSearchError(null);
  };

  const closeAddModal = () => {
    setAddModalDate(null);
    setSearchResults([]);
    setSearchQuery('');
    setAddModalTopic('');
    setSearchError(null);
  };

  const runSearch = async () => {
    const query = searchQuery.trim();
    const topic = addModalTopic.trim();

    if (!query && !topic) {
      setSearchError('Select a topic to browse the pool, or enter a search term.');
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError(null);
      const params: Record<string, string | number> = {
        page: 1,
        limit: 30
      };
      if (topic) params.mainTopic = topic;
      if (query) params.search = query;

      const res = await axios.get<{
        success: boolean;
        data: { questions: QuestionSearchResult[] };
        message?: string;
      }>('/api/admin/questions', { params });

      if (!res.data.success) {
        throw new Error(res.data.message || 'Failed to search questions');
      }
      setSearchResults(res.data.data.questions || []);
    } catch (err: any) {
      setSearchError(err?.response?.data?.message || err?.message || 'Failed to search questions.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    runSearch();
  };

  useEffect(() => {
    loadUpcoming();
  }, [loadUpcoming]);

  useEffect(() => {
    if (addModalDate) {
      document.body.classList.add('admin-upcoming-modal-open');
    } else {
      document.body.classList.remove('admin-upcoming-modal-open');
    }
    return () => {
      document.body.classList.remove('admin-upcoming-modal-open');
    };
  }, [addModalDate]);

  return (
    <div className="admin-page">
      <SEOHead
        title="Admin | Upcoming"
        description="Manage upcoming quiz question sets."
      />
      <header className="admin-page__header">
        <h1 className="admin-page__title">Upcoming</h1>
        <p className="admin-page__subtitle">
          Next {UPCOMING_WINDOW_DAYS} days of quiz question sets (5 topics × 10 questions per day). Click a row to expand and view questions grouped by topic. Rebuild or add questions for future dates.{' '}
          <Link to="/admin/competitive" className="link">View quiz history →</Link>
        </p>
      </header>

      <div className="admin-page__filters">
        <button type="button" className="btn btn-secondary btn-xs" onClick={loadUpcoming} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="admin-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : dates.length === 0 ? (
        <p className="admin-loading">No upcoming quizzes.</p>
      ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ minWidth: 400 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Questions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dates.map((entry) => {
                  const isExpanded = expandedDate === entry.date;
                  const isActiveDate = Boolean(todayDate && entry.date === todayDate);
                  const questionCount = entry.questions.length;
                  return (
                    <React.Fragment key={entry.date}>
                      <tr
                        role="button"
                        tabIndex={0}
                        aria-label={`Expand ${entry.date} (${questionCount} questions)`}
                        onClick={() => toggleRow(entry.date)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleRow(entry.date);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <span>{entry.date}</span>
                          {isActiveDate && <span className="admin-badge admin-badge--medium" style={{ marginLeft: 'var(--space-2)' }}>Today</span>}
                        </td>
                        <td>
                          <span style={{ fontWeight: questionCount === EXPECTED_QUESTIONS_PER_DAY ? 'normal' : 'bold', color: questionCount === EXPECTED_QUESTIONS_PER_DAY ? 'inherit' : 'var(--color-error)' }}>
                            {questionCount}
                          </span>
                          {questionCount > 0 && entry.questions.some((q) => q.mainTopic) && (
                            <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }} title={getTopicCounts(entry.questions).map((t) => `${t.topic}: ${t.count}`).join(', ')}>
                              ({getTopicCounts(entry.questions).map((t) => t.count).join('/')})
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn btn-primary btn-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                rebuildDate(entry.date);
                              }}
                              disabled={rebuildingDate === entry.date || isActiveDate}
                              title={isActiveDate ? 'Today is view-only' : 'Rebuild question set'}
                            >
                              {rebuildingDate === entry.date ? 'Rebuilding...' : 'Rebuild set'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                openAddModal(entry.date);
                              }}
                              disabled={isActiveDate}
                              title={isActiveDate ? 'Today is view-only' : 'Add question to set'}
                            >
                              Add question
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={3} className="admin-table-detail">
                            {entry.questions.length === 0 ? (
                              <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>No questions assigned.</p>
                            ) : (
                              Array.from(groupQuestionsByTopic(entry.questions).entries()).map(([topicName, topicQuestions]) => (
                                <div key={topicName} style={{ marginBottom: 'var(--space-6)' }}>
                                  <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text)' }}>
                                    {topicName} <span style={{ fontWeight: 'normal', color: 'var(--color-text-muted)' }}>({topicQuestions.length}/10)</span>
                                  </h3>
                                  <table className="admin-table" style={{ marginBottom: 0 }}>
                                    <thead>
                                      <tr>
                                        <th>Question</th>
                                        <th>Subtopic</th>
                                        <th>Answer</th>
                                        <th>Last asked</th>
                                        <th>Difficulty</th>
                                        <th />
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {topicQuestions.map((question) => (
                                        <tr key={question.id}>
                                          <td style={{ maxWidth: 280 }}>{question.questionText}</td>
                                          <td>{question.category}</td>
                                          <td><strong>{question.correctAnswer}</strong></td>
                                          <td>{formatLastAskedDate(question.lastAskedDate)}</td>
                                          <td><span className={`admin-badge admin-badge--${question.difficulty}`}>{question.difficulty}</span></td>
                                          <td>
                                            <button
                                              type="button"
                                              className="btn btn-ghost btn-xs"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                removeQuestion(entry.date, question.id);
                                              }}
                                              disabled={workingDate === entry.date || isActiveDate}
                                              title={isActiveDate ? 'Today is view-only' : 'Remove question'}
                                            >
                                              Remove
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ))
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {addModalDate && (() => {
        const entry = dates.find((d) => d.date === addModalDate);
        const topicCounts = entry ? getTopicCounts(entry.questions) : [];
        const fullTopics = new Set(topicCounts.filter((t) => t.count >= 10).map((t) => t.topic));
        const availableTopics = topicCounts.filter((t) => t.count < 10).map((t) => `${t.topic} (${t.count}/10)`);
        const isTopicFull = (mainTopic: string | null) => mainTopic && fullTopics.has(mainTopic);

        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 'var(--space-8)', overflowY: 'auto', zIndex: 1000 }} onClick={closeAddModal}>
          <div className="admin-card" style={{ maxWidth: 640, width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ margin: 0 }}>Add question to {addModalDate}</h2>
              <button type="button" className="btn btn-ghost btn-xs" onClick={closeAddModal}>Close</button>
            </div>
            {availableTopics.length < 5 && availableTopics.length > 0 && (
              <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                Topics with available slots: {availableTopics.join(', ')}
              </p>
            )}
            {entry && entry.questions.length === 0 && (
              <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                No questions assigned. Select a topic below to browse the pool and add questions manually (10 per topic).
              </p>
            )}
            {availableTopics.length === 0 && entry && entry.questions.length >= EXPECTED_QUESTIONS_PER_DAY && (
              <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                All topics are full (50/50). Remove a question first to add a new one.
              </p>
            )}
            <form onSubmit={handleSearchSubmit} style={{ marginBottom: 'var(--space-4)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <select
                  value={addModalTopic}
                  onChange={(e) => setAddModalTopic(e.target.value)}
                  style={{ padding: 'var(--space-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', minWidth: 180 }}
                  aria-label="Filter by topic"
                >
                  <option value="">Topic (required to browse)</option>
                  {MAIN_TOPICS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Optional: search within topic…"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  style={{ flex: 1, minWidth: 160, padding: 'var(--space-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}
                />
                <button type="submit" className="btn btn-primary btn-xs" disabled={searchLoading}>
                  {searchLoading ? 'Loading...' : 'Browse pool'}
                </button>
              </div>
            </form>
            {searchError && <div className="admin-error" style={{ marginBottom: 'var(--space-3)' }}>{searchError}</div>}
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {searchResults.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)' }}>{searchLoading ? 'Searching…' : 'No results.'}</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Category</th>
                      <th>Answer</th>
                      <th>Last asked</th>
                      <th>Difficulty</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((result) => {
                      const topicFull = isTopicFull(result.main_topic ?? null);
                      const alreadyAssigned = entry?.questions.some((q) => q.id === result.id) ?? false;
                      const addDisabled = workingDate === addModalDate || topicFull || alreadyAssigned;
                      const title = alreadyAssigned
                        ? 'Already in set'
                        : topicFull
                          ? `${result.main_topic ?? 'Topic'} already has 10 questions`
                          : 'Add to set';
                      return (
                      <tr key={result.id}>
                        <td style={{ maxWidth: 200 }}>{result.question_text}</td>
                        <td>{result.category}</td>
                        <td><strong>{result.correct_answer}</strong></td>
                        <td>{formatLastAskedDate(result.last_asked_date)}</td>
                        <td><span className={`admin-badge admin-badge--${result.difficulty}`}>{result.difficulty}</span></td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            onClick={() => addQuestion(addModalDate, result.id)}
                            disabled={addDisabled}
                            title={title}
                          >
                            {alreadyAssigned ? 'In set' : 'Add'}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <button type="button" className="btn btn-ghost btn-xs" onClick={closeAddModal}>Done</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default AdminUpcomingCompetitive;
